import { useNavigate } from "react-router";

import { toast } from "@jti/core-components/components/Toaster";
import { SinglePageCheckoutRoutes } from "@jti/fe-common";
import { useTranslations } from "@jti/ui";

import { usePaymentProviderContext } from "@checkout-v2/features/payment/context/PaymentProviderContext";

type UsePayForOrderParams = {
  orderNumber?: string;
  entityId?: string;
  paymentMethodCode?: string;
  isSubscription: boolean;
  securedFieldsPayment?: {
    sessionId: string;
    paymentMethodCode: string;
    tokenize: boolean;
  };
};

type UsePayForOrderReturn = {
  handlePaymentProcess: (params: UsePayForOrderParams) => Promise<void>;
};

const isSecuredFieldsPaymentSuccessful = (status: string): boolean => status === "paid" || status === "authorised";

export const usePayForOrder = (): UsePayForOrderReturn => {
  const { __ } = useTranslations();
  const { adapterStatus, initiatePayment, preloadAdapter, processSecuredFieldsPayment } = usePaymentProviderContext();
  const navigate = useNavigate();

  const handlePaymentProcess = async ({
    isSubscription,
    orderNumber,
    entityId,
    paymentMethodCode,
    securedFieldsPayment,
  }: UsePayForOrderParams) => {
    if (!orderNumber || !entityId || !paymentMethodCode) {
      toast.error(__("order.payment_form.error.missing_order_data"));
      return;
    }

    try {
      if (securedFieldsPayment?.paymentMethodCode === paymentMethodCode) {
        const orderId = Number(entityId);

        if (!Number.isInteger(orderId)) {
          throw new Error("Order entity ID must be an integer for secured fields payment");
        }

        const payment = await processSecuredFieldsPayment({
          sessionId: securedFieldsPayment.sessionId,
          orderId,
          paymentMethod: paymentMethodCode,
          tokenize: securedFieldsPayment.tokenize,
        });

        if (!isSecuredFieldsPaymentSuccessful(payment.status)) {
          throw new Error(`Secured fields payment was not successful: ${payment.status || "unknown"}`);
        }

        navigate(SinglePageCheckoutRoutes.getThankYouPagePath(orderNumber));
        return;
      }

      if (adapterStatus !== "ready") {
        await preloadAdapter(paymentMethodCode);
      }

      await initiatePayment({
        orderId: orderNumber,
        orderEntityId: entityId,
        paymentMethodCode,
        isSubscription,
      });
    } catch {
      const failurePath = SinglePageCheckoutRoutes.getOrderFailedPaymentPath(orderNumber);

      navigate(failurePath);

      window.scrollTo(0, 0);
    }
  };

  return { handlePaymentProcess };
};
